import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Box } from '@chakra-ui/react';

// Cloudflare Turnstile captcha widget.
//
// The token is optional on the backend: if the script fails to load (ad
// blocker, network) the form must still submit with an empty token, so this
// component never blocks the parent form. Tokens are single-use — the parent
// should call ref.current.reset() after every failed submission.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
export const TURNSTILE_SITEKEY =
  process.env.REACT_APP_TURNSTILE_SITEKEY || '0x4AAAAAADbfN1a5itlZdtkQ';

let scriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const onLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded without window.turnstile'));
    };
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC.split('?')[0]}"]`);
    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = onLoad;
    script.onerror = () => {
      scriptPromise = null; // allow a retry on next mount
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

const TurnstileWidget = forwardRef(({ onToken, sitekey = TURNSTILE_SITEKEY, ...boxProps }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current && onTokenRef.current('');
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (e) {
          // widget gone — nothing to reset
        }
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    const emit = (token) => onTokenRef.current && onTokenRef.current(token || '');

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey,
          callback: (token) => emit(token),
          'expired-callback': () => emit(''),
          'error-callback': () => emit(''),
        });
      })
      .catch((err) => {
        // Never block the user: the form submits without a token.
        console.warn('Turnstile unavailable:', err && err.message);
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // already removed
        }
      }
      widgetIdRef.current = null;
    };
  }, [sitekey]);

  return <Box ref={containerRef} display="flex" justifyContent="center" w="full" {...boxProps} />;
});

TurnstileWidget.displayName = 'TurnstileWidget';

// Pull a user-facing message out of a captcha/lockout error response
// (403 captcha_required/captcha_failed, 429 login_locked). Returns null for
// anything else so callers keep their existing messages.
export const getCaptchaErrorDetail = (error) => {
  const status = error?.response?.status ?? error?.status;
  const data = error?.response?.data ?? error?.data;
  if ((status === 403 || status === 429) && data && data.detail) {
    return data.detail;
  }
  return null;
};

export default TurnstileWidget;
